import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Chip,
    IconButton,
    Pagination,
    Breadcrumbs,
    Link as MuiLink
} from '@mui/material';
import { Print as PrintIcon, Edit as EditIcon } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { getDeliverySheets, dispatchDeliverySheet, checkInDeliverySheet, printDeliverySheet, DeliverySheet } from '../../services/deliverySheetApi';
import GenericModal from '../../components/GenericModal';
import useToast from '../../hooks/useToast';
import Toast from '../../components/Toast';

const DeliverySheets = () => {
    const { theme } = useTheme();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [deliverySheets, setDeliverySheets] = useState<DeliverySheet[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const { toast, showSuccess, showError, hideToast } = useToast();
    const [confirmModal, setConfirmModal] = useState<{ open: boolean; title: string; message: string; onConfirm: () => void }>({ open: false, title: '', message: '', onConfirm: () => {} });
    const [submitting, setSubmitting] = useState(false);
    const checkIn = searchParams.get('check-in');

    useEffect(() => {
        fetchDeliverySheets();
    }, [page, checkIn]);

    const fetchDeliverySheets = async () => {
        try {
            setLoading(true);
            const filters = {
                page,
                per_page: 20,
                ...(checkIn && { check_in: checkIn })
            };
            const response = await getDeliverySheets(filters);
            setDeliverySheets(response.data.data);
            setTotalPages(response.data.last_page || 1);
        } catch (error: any) {
            console.error('Error fetching delivery sheets:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDispatch = (id: number) => {
        setConfirmModal({
            open: true,
            title: 'Dispatch Delivery Sheet',
            message: 'Are you sure you want to dispatch this delivery sheet?',
            onConfirm: async () => {
                setSubmitting(true);
                try {
                    await dispatchDeliverySheet(id);
                    setConfirmModal(prev => ({ ...prev, open: false }));
                    showSuccess('Delivery sheet dispatched successfully!');
                    fetchDeliverySheets();
                } catch (error: any) {
                    showError(error.message || 'Failed to dispatch delivery sheet');
                } finally {
                    setSubmitting(false);
                }
            },
        });
    };

    const handleCheckIn = (id: number) => {
        setConfirmModal({
            open: true,
            title: 'Check In Delivery Sheet',
            message: 'Are you sure you want to check in this delivery sheet?',
            onConfirm: async () => {
                setSubmitting(true);
                try {
                    await checkInDeliverySheet(id);
                    setConfirmModal(prev => ({ ...prev, open: false }));
                    showSuccess('Delivery sheet checked in successfully!');
                    navigate(`/delivery-sheets/${id}`);
                } catch (error: any) {
                    showError(error.message || 'Failed to check in delivery sheet');
                } finally {
                    setSubmitting(false);
                }
            },
        });
    };

    const getStatusColor = (statusName: string) => {
        const lower = statusName.toLowerCase();
        if (lower.includes('created')) return 'warning';
        if (lower.includes('dispatch')) return 'info';
        if (lower.includes('checked')) return 'primary';
        if (lower.includes('closed')) return 'success';
        return 'default';
    };

    return (
        <Box>
            <Breadcrumbs sx={{ mb: 3 }}>
                <MuiLink
                    component="button"
                    variant="body1"
                    onClick={() => navigate('/dashboard')}
                    sx={{ textDecoration: 'none', color: theme.palette.text.primary }}
                >
                    Home
                </MuiLink>
                <Typography color="text.primary">
                    {checkIn ? 'Courier Check-In' : 'Delivery Sheets'}
                </Typography>
            </Breadcrumbs>

            <Card>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Typography variant="h5" sx={{ fontWeight: 600 }}>
                            {checkIn ? 'Courier Check-In' : 'Delivery Sheets'}
                        </Typography>
                        {!checkIn && (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => navigate('/delivery-sheets/create')}
                            >
                                Allocate Deliveries
                            </Button>
                        )}
                    </Box>

                    <TableContainer component={Paper} variant="outlined">
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Delivery Sheet ID</TableCell>
                                    <TableCell>Rider</TableCell>
                                    <TableCell>Consignments</TableCell>
                                    <TableCell>COD Amount</TableCell>
                                    <TableCell>Received Amount</TableCell>
                                    <TableCell>Collected Amount</TableCell>
                                    <TableCell>Created By</TableCell>
                                    <TableCell>Created At</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={10} align="center">Loading...</TableCell>
                                    </TableRow>
                                ) : deliverySheets.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={10} align="center">No records found</TableCell>
                                    </TableRow>
                                ) : (
                                    deliverySheets.map((sheet) => (
                                        <TableRow key={sheet.id}>
                                            <TableCell>
                                                <MuiLink
                                                    component="button"
                                                    onClick={() => navigate(`/delivery-sheets/${sheet.id}`)}
                                                    sx={{ textDecoration: 'none' }}
                                                >
                                                    {sheet.unique_id}
                                                </MuiLink>
                                            </TableCell>
                                            <TableCell>{sheet.rider?.name}</TableCell>
                                            <TableCell>
                                                Total: {sheet.total_consignments}<br />
                                                Delivered: {sheet.delivered_count || 0}<br />
                                                Rejected: {sheet.rejected_count || 0}<br />
                                                Reattempt: {sheet.reattempt_count || 0}
                                            </TableCell>
                                            <TableCell>{sheet.total_cod_amount}</TableCell>
                                            <TableCell>{sheet.cod_amount_recieved}</TableCell>
                                            <TableCell>
                                                {sheet.cod_amount_collected}<br />
                                                {sheet.collectedBy && <>By: {sheet.collectedBy.name}<br /></>}
                                                {sheet.cod_amount_collected_at && <>Date: {new Date(sheet.cod_amount_collected_at).toLocaleString()}</>}
                                            </TableCell>
                                            <TableCell>{sheet.user?.name}</TableCell>
                                            <TableCell>{new Date(sheet.created_at).toLocaleString()}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={sheet.status.name}
                                                    color={getStatusColor(sheet.status.name) as any}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <IconButton size="small" color="info" onClick={() => printDeliverySheet(sheet.id)}>
                                                    <PrintIcon />
                                                </IconButton>
                                                {sheet.status_id === 8 && (
                                                    <>
                                                        <IconButton size="small" color="primary" onClick={() => navigate(`/delivery-sheets/${sheet.id}/edit`)}>
                                                            <EditIcon />
                                                        </IconButton>
                                                        <Button size="small" variant="contained" color="warning" onClick={() => handleDispatch(sheet.id)} sx={{ ml: 1 }}>
                                                            Dispatch
                                                        </Button>
                                                    </>
                                                )}
                                                {sheet.status_id === 21 && (
                                                    <Button size="small" variant="contained" color="warning" onClick={() => handleCheckIn(sheet.id)}>
                                                        Check-in
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {totalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={(e, value) => setPage(value)}
                                color="primary"
                            />
                        </Box>
                    )}
                </CardContent>
            </Card>
            <GenericModal
                open={confirmModal.open}
                onClose={() => setConfirmModal(prev => ({ ...prev, open: false }))}
                title={confirmModal.title}
                maxWidth="xs"
                actions={
                    <>
                        <Button
                            onClick={() => setConfirmModal(prev => ({ ...prev, open: false }))}
                            disabled={submitting}
                            sx={{ color: theme.palette.text.secondary, borderRadius: 3 }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="success"
                            onClick={confirmModal.onConfirm}
                            disabled={submitting}
                            sx={{ borderRadius: 3 }}
                        >
                            {submitting ? 'Processing...' : 'Yes, Proceed'}
                        </Button>
                    </>
                }
            >
                <Typography variant="body1" color="text.primary">
                    {confirmModal.message}
                </Typography>
            </GenericModal>

            <Toast
                open={toast.open}
                message={toast.message}
                title={toast.title}
                severity={toast.severity}
                onClose={hideToast}
            />
        </Box>
    );
};

export default DeliverySheets;



